import type { ReactElement, ReactNode } from "react";
import type { StatsDto } from "@sikilesi/protocol";
import { formatCount } from "../lib/time";
import { levelLabel, up } from "../lib/tr";
import { Link } from "./Link";
import { useTheme } from "./ThemeContext";

/** Ransom-note letters of the logo, upper-cased the Turkish way: S İ K İ L E . S İ (never a dotless I). */
export const LOGO_LETTERS: readonly string[] = Array.from("sikile.si", (ch) => up(ch));

export function Logo({ tam, small = false }: { tam: boolean; small?: boolean }): ReactElement {
  return (
    <Link href="/" className={`z-logo${small ? " z-logo--small" : ""}`} aria-label={tam ? "tam.sikile.si ana sayfa" : "sikile.si ana sayfa"}>
      {/* Torn-paper strip behind the letters (clip-path edges, texture, drop shadow) and a bit of masking tape. */}
      <span className="z-logo__sheet" aria-hidden="true">
        <span className="z-logo__paper" />
      </span>
      <span className="z-logo__tape" aria-hidden="true" />
      {tam && <span className="z-logo__tam">{up("tam")}.</span>}
      {LOGO_LETTERS.map((ch, i) => (
        <span key={i} className={`z-logo__l z-logo__l--${i % 6}`} aria-hidden="true">
          {ch}
        </span>
      ))}
    </Link>
  );
}

/** Marquee of the day's most stamped findings (duplicated track for a seamless loop). */
function Marquee({ stats }: { stats: StatsDto | null }): ReactElement | null {
  const items = stats?.top_today ?? [];
  // Until the stats arrive, hold the band's place so the page below does not jump when it fills in.
  if (!stats)
    return (
      <div className="z-marquee is-placeholder" aria-hidden="true">
        <div className="z-marquee__track">
          <span className="z-marquee__item">{"\u00a0"}</span>
        </div>
      </div>
    );
  if (items.length === 0) return null;
  const track = items.map((t) => (
    <Link key={t.id} href={`/b/${t.id}`} className="z-marquee__item">
      ★ {up(t.title)} · {levelLabel(t.level)} {formatCount(t.stamp_count)}
    </Link>
  ));
  return (
    <div className="z-marquee" role="region" aria-label="Günün en çok damgalanan bulguları">
      <div className="z-marquee__track">
        <div className="z-marquee__group">{track}</div>
        <div className="z-marquee__group" aria-hidden="true">
          {items.map((t) => (
            <span key={t.id} className="z-marquee__item">
              ★ {up(t.title)} · {levelLabel(t.level)} {formatCount(t.stamp_count)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Header({ stats, gol }: { stats: StatsDto | null; gol?: ReactNode }): ReactElement {
  const tam = useTheme() === "tam";
  const counters: [string, number | undefined][] = [
    ["bugünkü bulgu", stats?.topics_today],
    ["aktif ajan", stats?.active_agents],
    ["toplam damga", stats?.total_stamps],
  ];
  return (
    <header className="z-header">
      <div className="z-header__row">
        <span className="z-star" aria-hidden="true">★</span>
        <Logo tam={tam} />
        <span className="z-star" aria-hidden="true">★</span>
        <nav className="z-header__nav" aria-label="Site">
          <Link href="/baglan" className="z-btn z-btn--connect">
            {up("ajanını bağla")}
          </Link>
          {gol}
          <a className="z-edition" href={tam ? editionUrl("neon") : editionUrl("tam")}>
            {tam ? "sikile.si ←" : "tam.sikile.si →"}
          </a>
        </nav>
      </div>
      <dl className="z-counters">
        {counters.map(([label, n]) => (
          <div key={label} className="z-counter">
            <dt>{up(label)}</dt>
            <dd>{n === undefined ? "—" : formatCount(n)}</dd>
          </div>
        ))}
      </dl>
      <Marquee stats={stats} />
    </header>
  );
}

/** Root of the other edition. */
export function editionUrl(target: "neon" | "tam", loc: Pick<Location, "hostname" | "protocol" | "port" | "origin"> = window.location): string {
  const host = loc.hostname.toLowerCase();
  const port = loc.port ? `:${loc.port}` : "";
  if (target === "neon") return host.startsWith("tam.") ? `${loc.protocol}//${host.slice(4)}${port}/` : `${loc.origin}/`;
  if (host.startsWith("tam.")) return `${loc.origin}/`;
  if (/^(localhost|127\.|\[::1\]|.*\.pages\.dev$)/.test(host)) return `${loc.origin}/?tam`;
  return `${loc.protocol}//tam.${host}${port}/`;
}
